import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Check, Ticket, ShoppingCart, TrendingUp, AlertTriangle, MessageSquare,
  CheckCheck, Inbox, Store, ShieldCheck,
} from "lucide-react";
import api from "../lib/api";
import { formatDateTime } from "../lib/format";

const ICON = {
  ticket_assigned: Ticket,
  ticket_status: Ticket,
  ticket_reply: MessageSquare,
  po_new: ShoppingCart,
  po_status: ShoppingCart,
  forecast_alert: AlertTriangle,
  supplier_baru: Store,
  supplier_verifikasi: ShieldCheck,
};

const SEV_BG = {
  danger:  "bg-red-50 text-red-600",
  warning: "bg-amber-50 text-amber-600",
  info:    "bg-blue-50 text-[#045498]",
  success: "bg-emerald-50 text-emerald-600",
};

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "Baru saja";
    if (diff < 3600) return `${Math.floor(diff / 60)} mnt lalu`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)} hari lalu`;
    return formatDateTime(iso);
  } catch { return ""; }
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread_count: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 60s for fresh notifications
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (!open) return;
      if (dropdownRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      await load();
    } catch (_) {}
  };

  const handleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) await load();
  };

  const handleClick = async (it) => {
    setOpen(false);
    // Hanya notifikasi yang dibuka yang ditandai dibaca; sisanya tetap belum dibaca.
    if (it.unread) {
      setData((d) => ({
        ...d,
        unread_count: Math.max(0, d.unread_count - 1),
        items: d.items.map((x) => (x.id === it.id ? { ...x, unread: false } : x)),
      }));
      try {
        await api.post("/notifications/read", { id: it.id });
      } catch (_) {}
    }
    if (it.link) navigate(it.link);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className="text-slate-500 hover:text-slate-900 p-2 rounded-md hover:bg-slate-100 relative"
        data-testid="notification-bell"
        aria-label="Pusat Notifikasi"
      >
        <Bell className="w-5 h-5" />
        {data.unread_count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center border-2 border-white"
            data-testid="notification-badge"
          >
            {data.unread_count > 99 ? "99+" : data.unread_count}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-[380px] max-w-[92vw] bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div>
              <div className="font-display font-semibold text-slate-900 text-sm">Pusat Notifikasi</div>
              <div className="text-xs text-slate-500">
                {data.unread_count > 0
                  ? `${data.unread_count} belum dibaca · ${data.total} total`
                  : `${data.total} notifikasi · semua sudah dibaca`}
              </div>
            </div>
            {data.unread_count > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-[#045498] hover:underline inline-flex items-center gap-1"
                data-testid="mark-all-read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Tandai semua dibaca
              </button>
            )}
          </div>

          <div className="max-h-[480px] overflow-y-auto" data-testid="notification-list">
            {loading && data.items.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">Memuat...</div>
            ) : data.items.length === 0 ? (
              <div className="p-10 text-center">
                <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <div className="text-sm text-slate-500">Belum ada notifikasi</div>
                <div className="text-xs text-slate-400 mt-1">
                  Aktivitas tiket, PO, dan alert harga akan muncul di sini.
                </div>
              </div>
            ) : (
              data.items.map((it) => {
                const Icon = ICON[it.type] || Bell;
                return (
                  <button
                    key={it.id}
                    onClick={() => handleClick(it)}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-start gap-3 transition-colors ${
                      it.unread ? "bg-blue-50/30" : ""
                    }`}
                    data-testid={`notif-item-${it.id}`}
                  >
                    <div className={`w-9 h-9 rounded-md ${SEV_BG[it.severity] || SEV_BG.info} flex items-center justify-center shrink-0 mt-0.5`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className={`text-sm leading-snug ${it.unread ? "font-semibold text-slate-900" : "font-medium text-slate-700"} line-clamp-2`}>
                          {it.title}
                        </div>
                        {it.unread && (
                          <span className="w-2 h-2 bg-[#045498] rounded-full shrink-0 mt-1.5" aria-label="unread" />
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{it.message}</div>
                      <div className="text-[11px] text-slate-400 mt-1">{timeAgo(it.created_at)}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {data.items.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-center">
              <span className="text-[11px] text-slate-500">
                Menampilkan aktivitas 14 hari terakhir
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
